import { LocalizedComponentProps } from 'bernie-l10n';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { UitkCard, UitkCardLink } from 'uitk-react-cards';
import { UitkCarousel } from 'uitk-react-carousel';
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from 'uitk-react-images';
import { UitkScrim } from 'uitk-react-scrim';
import { UitkHeading } from 'uitk-react-text';
import { ActivityDestinationViewSearch } from '../../../../common/__generated__/api';
import { getSeeAllURL } from '../../../../helpers/common-helper';
import { onClickScrollToTarget } from '../../../../helpers/smooth-scroll';
import { useStores } from '../../../../utils/hooks';

export interface CategoryCarouselProps extends LocalizedComponentProps {
  categoryGroup: ActivityDestinationViewSearch.ActivityGroups;
}

export const CategoryCarousel: React.FC<CategoryCarouselProps> = ({ categoryGroup }) => {
  const { routeStore, analytics } = useStores();
  const { activityTiles, name, action } = categoryGroup;

  const handleShowAllFiltersClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
    event.preventDefault();
    analytics.trackEvent('LX.Des.CategoryCarousel.ShowAll', 'show-all-categories-clicked', analytics.omnitureData);
    routeStore.updateSearch({ pwaDialog: 'filter' });
  };

  const handleCategoryClick = (
    category: ActivityDestinationViewSearch.ActivityBasicTileInlineFragment
  ): void | null => {
    if (!category.analytics) {
      return null;
    }
    onClickScrollToTarget({ scrollTo: 0 });
    analytics.trackEvent(category.analytics.referrerId, category.analytics.linkName, analytics.omnitureData);
  };

  const renderCategoryCard = (
    category: ActivityDestinationViewSearch.ActivityBasicTileInlineFragment,
    index: number
  ): JSX.Element | null => {
    if (!category.image) {
      return null;
    }

    return (
      <UitkCard key={`${name}-category-carousel-card-${index}`} data-testid={`${name}-category-carousel-card-${index}`}>
        <UitkFigure ratio={UitkFigureAspectRatioType.R3_4}>
          <UitkImage alt={category.image.description} src={category.image.url} />
          <UitkScrim>
            <p className="uitk-type-center uitk-type-300 uitk-type-bold" aria-hidden="true">
              {category.name}
            </p>
          </UitkScrim>
        </UitkFigure>
        <UitkCardLink>
          <Link
            onClick={handleCategoryClick.bind(null, category)}
            to={getSeeAllURL(category.link, routeStore.queryParams)}
          >
            {category.name}
          </Link>
        </UitkCardLink>
      </UitkCard>
    );
  };

  const renderShowAllCategoriesTile = (action: ActivityDestinationViewSearch.__Action): JSX.Element => (
    <UitkCard>
      <a
        id="show-all-categories-link"
        onClick={handleShowAllFiltersClick}
        href={action.url}
        className="uitk-button-fullWidth all-x-padding-three uitk-type-center uitk-type-300"
        data-testid={`${name}-category-carousel-show-all-things-to-do--link`}
      >
        {action.label}
      </a>
    </UitkCard>
  );

  const renderCategoryTiles = (): Array<JSX.Element | null> =>
    activityTiles.map((category, index) =>
      renderCategoryCard(category as ActivityDestinationViewSearch.ActivityBasicTileInlineFragment, index)
    );

  const renderCarouselCards = (action: ActivityDestinationViewSearch.__Action): Array<JSX.Element | null> => {
    const carouselCards = action
      ? [...renderCategoryTiles(), renderShowAllCategoriesTile(action)]
      : renderCategoryTiles();

    return carouselCards;
  };

  return (
    <section className="all-t-margin-six all-x-margin-three" data-testid={`${name}-category-carousel--section`}>
      <UitkHeading tag="h2" size={4} className="all-b-margin-four">
        {name}
      </UitkHeading>
      {activityTiles && activityTiles.length > 0 && (
        <UitkCarousel
          carouselName={`${name}-category-carousel`}
          itemsVisible={{
            lg: 3,
            md: 2,
            sm: 2
          }}
          pageBy={1}
          peekWidth={12}
          buttonText={{
            nextButton: 'next',
            prevButton: 'previous'
          }}
          navigationIconId={{
            next: 'category-carousel-next-button-title',
            prev: 'category-carousel-prev-button-title'
          }}
          wrapItems={false}
        >
          {renderCarouselCards(action as ActivityDestinationViewSearch.__Action)}
        </UitkCarousel>
      )}
    </section>
  );
};
